import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { withFlags } from '../../../flags/Flags';
import * as StringUtils from '../../../utilities/stringUtils';

import {
  Button,
  Checkbox,
  Combobox,
  Input,
  Radio,
  RadioGroup,
  RadioButtonGroup,
  Textarea,
  Tooltip
} from '@salesforce/design-system-react';

import './AssetClassEditPane.scss';

class AssetClassEditPane extends Component {
  optionFor(assetClass) {
    return {
      id: assetClass.uuid,
      label: assetClass.name,
      value: assetClass.uuid
    };
  }
  childClassOptions() {
    return (this.props.types || [])
      .filter(
        type =>
          !type.parent_class_id && type.uuid !== this.props.assetClass?.uuid
      )
      .sort(StringUtils.sortByField('name', this.props.intl.locale))
      .map(this.optionFor.bind(this));
  }

  renderParentChild(assetClass) {
    const { formatMessage } = this.props.intl;
    const label = formatMessage({ id: 'components.ecs.classes.nestUnder' });
    const parentCombobox = (
      <Combobox
        className="nest-combobox"
        options={this.childClassOptions()}
        selection={assetClass?.parentSelection || []}
        events={{
          onSelect: (evt, { selection }) =>
            this.props.onChange({ parentSelection: selection })
        }}
        labels={{
          placeholderReadOnly: formatMessage({
            id: 'components.ecs.classes.placeholder'
          })
        }}
        variant="readonly"
      />
    );
    const nesting = (
      <div className="nesting">
        <Checkbox
          className="nest-checkbox"
          labels={{ label }}
          value={assetClass?.isChild || false}
          checked={assetClass?.isChild || false}
          disabled={!!assetClass?.child_ids?.length}
          onChange={(evt, { checked }) => {
            const updates = { isChild: checked };
            if (!checked) {
              updates.parentSelection = undefined;
            } else {
              updates.control_subject_line = false;
            }
            this.props.onChange(updates);
          }}
        />
        {assetClass?.isChild ? parentCombobox : null}
      </div>
    );

    return withFlags(flags => {
      if (flags.parentChild) {
        return nesting;
      }
      return null;
    });
  }

  saveAllowed(flags) {
    return !(flags && flags.isReadonly);
  }

  renderSubjectLine(assetClass) {
    const { formatMessage } = this.props.intl;
    const label = formatMessage({
      id: 'components.ecs.classes.subjectLineCheckbox.label'
    });
    const subjectLineCheckbox = (
      <div className="subject-line with-tooltip">
        <Checkbox
          className="subject-line-checkbox"
          labels={{ label }}
          value={assetClass?.control_subject_line || false}
          checked={assetClass?.control_subject_line || false}
          disabled={this.props.assetClass.isChild}
          onChange={(evt, { checked }) => {
            this.props.onChange({ control_subject_line: checked });
          }}
        />
        <Tooltip
          triggerStyle={{ lineHeight: null }}
          content={formatMessage({
            id: 'components.ecs.classes.subjectLineCheckbox.tooltip'
          })}
        />
      </div>
    );

    return withFlags(flags => {
      if (flags.subjectLine) {
        return subjectLineCheckbox;
      }
      return null;
    });
  }

  renderWeatherSettings(assetClass) {
    if (!this.props.rules?.settings?.weather) return null;

    const { formatMessage } = this.props.intl;
    const awareLabel = formatMessage({
      id: 'components.ecs.classes.weather.aware'
    });
    const forecastLabel = formatMessage({
      id: 'components.ecs.classes.weather.forecast.label'
    });
    const forecastDaysRadioGroup = (
      <RadioButtonGroup
        className="weather-forecast-days"
        labels={{ label: forecastLabel }}
        onChange={event =>
          this.props.onChange({
            weather_forecast_days: parseInt(event.target.value)
          })
        }
      >
        {this.forecastRadioButton(assetClass, 1)}
        {this.forecastRadioButton(assetClass, 5)}
        {this.forecastRadioButton(assetClass, 10)}
        {this.forecastRadioButton(assetClass, 15)}
      </RadioButtonGroup>
    );

    const weatherOptions = (
      <div className="weather-options">
        {forecastDaysRadioGroup}
        {this.excludeEmptyRadioGroup(assetClass)}
      </div>
    );

    return (
      <div className="weather">
        <div className="with-tooltip">
          <Checkbox
            className="weather-aware-checkbox"
            labels={{ label: awareLabel }}
            value={assetClass?.weather_aware || false}
            checked={assetClass?.weather_aware || false}
            onChange={(evt, { checked }) => {
              this.props.onChange({
                weather_aware: checked,
                // defaults to 1 day when box is checked for the first time
                weather_forecast_days: checked
                  ? assetClass?.weather_forecast_days || 1
                  : assetClass?.weather_forecast_days,
                weather_exclude_empty:
                  assetClass?.weather_exclude_empty === 'true'
              });
            }}
          />
          <Tooltip
            triggerStyle={{ lineHeight: null }}
            content={formatMessage({
              id: 'components.ecs.classes.weather.tooltip'
            })}
          />
        </div>
        {assetClass?.weather_aware ? weatherOptions : null}
      </div>
    );
  }

  forecastRadioButton(assetClass, days) {
    const { formatMessage } = this.props.intl;
    return (
      <Radio
        key={`${days}-days`}
        id={`${days}-days`}
        labels={{
          label: formatMessage(
            {
              id: 'components.ecs.classes.weather.forecast.days'
            },
            { days }
          )
        }}
        value={`${days}`}
        variant="button-group"
        checked={assetClass?.weather_forecast_days === days}
      />
    );
  }

  excludeEmptyRadioGroup(assetClass) {
    const { formatMessage } = this.props.intl;
    const excludeEmpty = assetClass?.weather_exclude_empty || false;
    return (
      <RadioGroup
        className="weather-exclude-empty"
        labels={{
          label: formatMessage({
            id: 'components.ecs.classes.weather.exclude.label'
          })
        }}
        onChange={event => {
          this.props.onChange({
            weather_exclude_empty: event?.target?.value
          });
        }}
      >
        {['false', 'true'].map(value => (
          <Radio
            key={`exclude-${value}`}
            id={`exclude-${value}`}
            labels={{
              label: formatMessage({
                id: `components.ecs.classes.weather.exclude.${value}`
              })
            }}
            value={`${value}`}
            checked={excludeEmpty.toString() === value}
          />
        ))}
      </RadioGroup>
    );
  }

  invalid() {
    const nameMissing = !this.props.assetClass?.name?.length;
    const weatherInvalid = this.props.assetClass.weather_aware
      ? !this.props.assetClass?.weather_forecast_days
      : false;
    return nameMissing || weatherInvalid;
  }

  render() {
    if (!this.props.isOpen) {
      return null;
    }
    const { formatMessage } = this.props.intl;
    const { assetClass } = this.props;

    const isCreate = !assetClass?.uuid;
    const title = isCreate
      ? 'components.ecs.classes.createTitle'
      : 'components.ecs.classes.editTitle';

    return withFlags(flags => {
      return (
        <React.Fragment>
          <div className="edit-pane">
            <div className="edit-title-box">
              <div className="edit-title">
                <FormattedMessage id={title} />
              </div>
              <Button
                className="close-filter"
                iconCategory="utility"
                iconName="close"
                iconSize="medium"
                variant="base"
                onClick={this.props.onClose}
              />
            </div>
            <div className="edit-pane-content">
              <Input
                required={isCreate}
                className="field-input"
                value={assetClass?.name || ''}
                disabled={!!assetClass?.uuid}
                label={formatMessage({
                  id: 'components.ecs.classes.edit.name'
                })}
                onChange={(_evt, { value }) =>
                  this.props.onChange({ name: value })
                }
              />
              <div className="description-input">
                <Textarea
                  className="field-input textarea-input"
                  value={assetClass?.description || ''}
                  label={formatMessage({
                    id: 'components.ecs.classes.edit.description'
                  })}
                  onChange={event =>
                    this.props.onChange({ description: event?.target?.value })
                  }
                />
              </div>
              <div className="class-settings">
                {this.renderWeatherSettings(assetClass)}
                {this.renderParentChild(assetClass)}
                {this.renderSubjectLine(assetClass)}
              </div>
            </div>
            <div className="edit-pane-footer">
              <Button
                label={formatMessage({ id: 'common.actions.cancel' })}
                onClick={this.props.onClose}
              />
              <Button
                label={formatMessage({ id: 'common.actions.save' })}
                onClick={this.props.onSave}
                variant="brand"
                disabled={this.invalid() || !this.saveAllowed(flags)}
              />
            </div>
          </div>
        </React.Fragment>
      );
    });
  }
}

AssetClassEditPane.propTypes = {
  assetClass: PropTypes.object,
  rules: PropTypes.object.isRequired,
  types: PropTypes.array,
  isOpen: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetClassEditPane);
